import React, { PureComponent } from 'react';

import { connect } from 'react-redux';

import {
  withAnalyticsEvents,
  WithAnalyticsEventsProps,
} from '@atlaskit/analytics-next';
import { fireScreenAnalytics } from '@atlassian/analytics-bridge';

import { uncurlyUuid } from 'src/components/analytics';
import { withApdex } from 'src/components/apdex/with-apdex';
import { withStatsd } from 'src/components/performance-metrics/with-statsd';
import { Repository } from 'src/components/types';
import { Ref } from 'src/sections/repository/types';
import { ApdexTask } from 'src/types/apdex';
import { BucketState, BucketDispatch } from 'src/types/state';
import { stopApdex } from 'src/utils/analytics/apdex';
import { statsdApiClient } from 'src/utils/metrics';
import waitForReactRender from 'src/utils/wait-for-react-render';

import fetchSourceDirMetadata from '../actions/fetch-source-dir-metadata';
import Directory from '../components/directory';
import { SrcDirMetadata, TreeEntry } from '../types';
import { getFirstReadme } from '../utils/readme';

const InstrumentedDirectory = withApdex(ApdexTask.Source)(
  withStatsd('performance.source.directory.rendered')(Directory)
);

type ConnectedSourceDirectoryProps = {
  contents: TreeEntry[];
  hash: string;
  path: string | null | undefined;
  repository: Repository;
} & WithAnalyticsEventsProps;

const mapDispatchToProps = (dispatch: BucketDispatch) => ({
  fetchSourceDirMetadata: (repository: Repository, ref: string, path: string) =>
    // @ts-ignore Thunk usage
    dispatch(fetchSourceDirMetadata(repository, ref, path)),
});

const mapStateToProps = (
  state: BucketState,
  ownProps: ConnectedSourceDirectoryProps
) => {
  const { hash, path } = ownProps;
  const { srcDirMetadata } = state.repository.source;

  return {
    srcDirMetadata: srcDirMetadata[`${hash}:${path || '/'}`],
    atRef: state.repository.source.section.atRef,
    maxItemsDisplayed: state.repository.source.section.maxItemsDisplayed || 500,
  };
};

type SourceDirectoryProps = ConnectedSourceDirectoryProps & {
  atRef: Ref | null | undefined;
  fetchSourceDirMetadata: (
    repository: Repository,
    ref: string,
    path: string
  ) => void;
  srcDirMetadata: SrcDirMetadata | null | undefined;
  maxItemsDisplayed: number;
};

export class SourceDirectory extends PureComponent<SourceDirectoryProps> {
  /* eslint react/sort-comp: "warn" */
  static screenName = 'repositorySourceScreen';

  componentDidMount() {
    this.fetchSourceDirMetadata();
    const { contents } = this.props;
    this.directoryChanged(contents);
  }

  componentDidUpdate(prevProps: SourceDirectoryProps) {
    this.fetchSourceDirMetadata();

    const { path, contents } = this.props;
    if (path !== prevProps.path) {
      this.directoryChanged(contents);
      waitForReactRender(() => {
        stopApdex(ApdexTask.Source);
      });
    }
  }

  fetchSourceDirMetadata() {
    const { hash, path, repository, srcDirMetadata } = this.props;
    if (
      !srcDirMetadata ||
      (!srcDirMetadata.isLoading && !srcDirMetadata.data)
    ) {
      this.props.fetchSourceDirMetadata(repository, hash, path || '/');
    }
  }

  directoryChanged(contents: TreeEntry[]) {
    const readme = getFirstReadme(contents);

    if (this.props.createAnalyticsEvent) {
      const workspaceId = this.props.repository.workspace?.uuid;
      fireScreenAnalytics(this.props.createAnalyticsEvent({}), {
        hasReadMe: readme !== undefined,
        repositoryId: uncurlyUuid(this.props.repository.uuid),
        workspaceId: workspaceId && uncurlyUuid(workspaceId),
      });
    }

    statsdApiClient.histogram({
      'frontbucket.directory.item_count': contents.length,
    });
  }

  render() {
    const {
      atRef,
      contents,
      path,
      repository,
      srcDirMetadata,
      maxItemsDisplayed,
    } = this.props;
    return (
      <InstrumentedDirectory
        contents={contents}
        srcDirMetadata={srcDirMetadata ? srcDirMetadata.data : undefined}
        path={path}
        repository={repository}
        atRef={atRef}
        maxItemsDisplayed={maxItemsDisplayed}
      />
    );
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  withAnalyticsEvents()(
    SourceDirectory as React.ComponentType<ConnectedSourceDirectoryProps>
  )
);
